import type { CSSProperties } from 'react';

type HslColor = {
  h: number;
  s: number;
  l: number;
};

type ThemeVariables = CSSProperties & Record<string, string>;

const HEX_REGEX = /^#?([a-f\d]{3}|[a-f\d]{6})$/i;
const LIGHT_FOREGROUND = '0 0% 98%';
const DARK_FOREGROUND = '0 0% 6%';
const clamp = (value: number, min = 0, max = 100) =>
  Math.min(Math.max(value, min), max);

const toHslString = ({ h, s, l }: HslColor) =>
  `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;

const toCssHsl = (color: HslColor) => `hsl(${toHslString(color)})`;

const getReadableForeground = (lightness: number) =>
  lightness > 60 ? DARK_FOREGROUND : LIGHT_FOREGROUND;

const shiftColor = (
  color: HslColor,
  shifts: { saturation?: number; lightness?: number }
): HslColor => ({
  h: color.h,
  s: clamp(color.s + (shifts.saturation ?? 0)),
  l: clamp(color.l + (shifts.lightness ?? 0)),
});

const normalizeHex = (value?: string) => {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(HEX_REGEX);

  if (!match) return null;

  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
  }

  return `#${hex.toLowerCase()}`;
};

const hexToHsl = (hexColor: string): HslColor | null => {
  if (!HEX_REGEX.test(hexColor.replace('#', ''))) {
    return null;
  }

  const normalizedHex = hexColor.replace('#', '');

  const r = parseInt(normalizedHex.slice(0, 2), 16) / 255;
  const g = parseInt(normalizedHex.slice(2, 4), 16) / 255;
  const b = parseInt(normalizedHex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
    } else if (max === g) {
      h = ((b - r) / delta + 2) * 60;
    } else {
      h = ((r - g) / delta + 4) * 60;
    }

    s = delta / (1 - Math.abs(2 * l - 1));
  }

  return {
    h,
    s: clamp(s * 100),
    l: clamp(l * 100),
  };
};

export type CatalogThemeMode = 'user' | 'light' | 'dark';

/** Page background tone for mobile browser chrome — aligned with globals.css surfaces. */
export const CATALOG_LIGHT_SURFACE_COLOR = '#ffffff';
export const CATALOG_DARK_SURFACE_COLOR = '#252529';

export type ViewportThemeColor =
  | string
  | Array<{ media: string; color: string }>;

export function resolveCatalogViewportThemeColor(
  mode: CatalogThemeMode
): ViewportThemeColor {
  if (mode === 'dark') {
    return CATALOG_DARK_SURFACE_COLOR;
  }
  if (mode === 'light') {
    return CATALOG_LIGHT_SURFACE_COLOR;
  }
  return [
    { media: '(prefers-color-scheme: light)', color: CATALOG_LIGHT_SURFACE_COLOR },
    { media: '(prefers-color-scheme: dark)', color: CATALOG_DARK_SURFACE_COLOR },
  ];
}

export function resolveAppleWebAppStatusBarStyle(
  mode: CatalogThemeMode,
  resolvedTheme?: string | null
): 'default' | 'black-translucent' {
  if (mode === 'dark') {
    return 'black-translucent';
  }
  if (mode === 'light') {
    return 'default';
  }
  return resolvedTheme === 'dark' ? 'black-translucent' : 'default';
}

export function resolveCatalogBrowserChrome(
  mode: CatalogThemeMode,
  resolvedTheme?: string | null
): {
  themeColor: string;
  colorScheme: 'light' | 'dark';
  appleStatusBarStyle: 'default' | 'black-translucent';
} {
  if (mode === 'light') {
    return {
      themeColor: CATALOG_LIGHT_SURFACE_COLOR,
      colorScheme: 'light',
      appleStatusBarStyle: 'default',
    };
  }
  if (mode === 'dark') {
    return {
      themeColor: CATALOG_DARK_SURFACE_COLOR,
      colorScheme: 'dark',
      appleStatusBarStyle: 'black-translucent',
    };
  }

  const isDark = resolvedTheme === 'dark';
  return {
    themeColor: isDark ? CATALOG_DARK_SURFACE_COLOR : CATALOG_LIGHT_SURFACE_COLOR,
    colorScheme: isDark ? 'dark' : 'light',
    appleStatusBarStyle: isDark ? 'black-translucent' : 'default',
  };
}

export const buildStoreThemeVariables = (
  color?: string | null
): ThemeVariables | undefined => {
  const normalized = normalizeHex(color ?? undefined);
  const hsl = normalized ? hexToHsl(normalized) : null;

  if (!hsl) {
    return undefined;
  }

  const primary = hsl;
  const ring = shiftColor(primary, { lightness: -4 });

  // Only override brand/action tokens. Neutral surfaces (secondary, muted,
  // border, input) stay on global :root values so they don't wash out against
  // the page background on light store colors.
  return {
    '--primary': toCssHsl(primary),
    '--primary-foreground': `hsl(${getReadableForeground(primary.l)})`,
    '--ring': toCssHsl(ring),
  };
};

export type { ThemeVariables };
